import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import './Leaves.css';

const LEAVE_TYPES = [
  { key: 'casual', label: 'Casual',  icon: '🌴', color: '#e11d48', desc: 'Short personal breaks' },
  { key: 'sick',   label: 'Sick',    icon: '🤒', color: '#10b981', desc: 'Health & recovery' },
  { key: 'annual', label: 'Annual',  icon: '✈️', color: '#3b82f6', desc: 'Planned vacation' },
];

function daysBetween(from, to) {
  if (!from) return 0;
  const f = new Date(from);
  const t = new Date(to || from);
  const diff = Math.round((t - f) / (1000 * 60 * 60 * 24)) + 1;
  return diff > 0 ? diff : 0;
}

function formatShortDate(d) {
  return new Date(d).toLocaleDateString('en-PK', { day: 'numeric', month: 'short' });
}

function formatFullDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-PK', { weekday: 'short', day: 'numeric', month: 'short' });
}

function formatLongDate(d) {
  if (!d) return '';
  return new Date(`${String(d).substring(0,10)}T00:00:00`)
    .toLocaleDateString('en-PK', { day: 'numeric', month: 'long', year: 'numeric' });
}

function relativeTime(d) {
  const days = Math.round((new Date(d) - new Date()) / (1000 * 60 * 60 * 24));
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  if (days > 1)  return `In ${days} days`;
  return `${Math.abs(days)} days ago`;
}

// A leave can only be cancelled while it is still upcoming — once the start
// date has arrived or passed it is history and must not be rewritten.
function isUpcoming(fromDate) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const from  = new Date(fromDate); from.setHours(0, 0, 0, 0);
  return from > today;
}

export default function Leaves({ employee }) {
  const [balance, setBalance]       = useState(null);
  const [history, setHistory]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [leaveType, setLeaveType]   = useState('casual');
  const [fromDate, setFromDate]     = useState('');
  const [toDate, setToDate]         = useState('');
  const [reason, setReason]         = useState('');
  const [message, setMessage]       = useState('');
  const [error, setError]           = useState('');
  const [showForm, setShowForm]     = useState(false);
  const [histFilter, setHistFilter] = useState('all'); // all | pending | approved | rejected | cancelled
  const [cancelling, setCancelling] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [balData, histData] = await Promise.all([
        api.getLeaveBalance(),
        api.getMyLeaves(new Date().getFullYear())
      ]);
      setBalance(balData.balance);
      setHistory(histData.leaves || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!fromDate || !reason) {
      setError('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    setError('');
    setMessage('');
    try {
      const res = await api.applyLeave({
        leave_type: leaveType,
        from_date: fromDate,
        to_date: toDate || fromDate,
        reason
      });
      if (res.error) {
        setError(res.error);
        return;
      }
      setMessage('Leave request submitted! HR will review within 24 hours.');
      setFromDate(''); setToDate(''); setReason('');
      setShowForm(false);
      loadData();
      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (leave) => {
    setCancelling(leave.id);
    setError('');
    try {
      const res = await api.cancelLeave(leave.id);
      if (res.error) {
        setError(res.error);
        setConfirmCancel(null);
        return;
      }
      setMessage('Leave cancelled. You are expected at work on those dates.');
      setConfirmCancel(null);
      loadData();
      setTimeout(() => setMessage(''), 5000);
    } catch {
      setError('Failed to cancel. Please try again.');
    } finally {
      setCancelling(null);
    }
  };

  const STATUS_CFG = {
    approved:  { label: 'Approved',  color: '#10b981', icon: '✓', bg: '#f0fdf4' },
    rejected:  { label: 'Rejected',  color: '#ef4444', icon: '✕', bg: '#fef2f2' },
    pending:   { label: 'Pending',   color: '#3b82f6', icon: '⏳', bg: '#eff6ff' },
    cancelled: { label: 'Cancelled', color: '#64748b', icon: '⊘', bg: '#f1f5f9' },
  };

  // ── PROBATION ──────────────────────────────────────────────────────
  // The server zeroes every entitlement during probation and refuses leave
  // applications outright. Rather than showing three empty balance rings
  // and a request form that will be rejected, the whole page becomes a
  // clear explanation of where they are and when that changes.
  const probation = balance?.probation || null;
  const onProbation = !!probation?.on_probation;

  // Progress through the probation period, for the bar.
  let probationPct = 0;
  if (onProbation && probation.joining_date && probation.probation_ends) {
    const start = new Date(`${probation.joining_date}T00:00:00`).getTime();
    const end   = new Date(`${probation.probation_ends}T00:00:00`).getTime();
    const now   = Date.now();
    probationPct = end > start
      ? Math.min(100, Math.max(0, Math.round(((now - start) / (end - start)) * 100)))
      : 0;
  }

  const balanceList = balance ? [
    { type: 'casual', remaining: balance.casual?.remaining || 0, total: balance.casual?.total || 3 },
    { type: 'sick',   remaining: balance.sick?.remaining   || 0, total: balance.sick?.total   || 5 },
    { type: 'annual', remaining: balance.annual?.remaining || 0, total: balance.annual?.total || 12 },
  ] : [];

  const requestedDays  = daysBetween(fromDate, toDate);
  const selectedMeta   = LEAVE_TYPES.find(t => t.key === leaveType);
  const selectedBal    = balanceList.find(b => b.type === leaveType);
  const exceedsBalance = selectedBal && requestedDays > selectedBal.remaining;

  const approvedCount = history.filter(h => h.status === 'approved').length;
  const pendingCount  = history.filter(h => h.status === 'pending').length;
  const rejectedCount = history.filter(h => h.status === 'rejected').length;

  const totalRemaining = balanceList.reduce((s, b) => s + b.remaining, 0);
  const totalDays      = balanceList.reduce((s, b) => s + b.total, 0);

  const upcoming = history
    .filter(h => h.status === 'approved' && new Date(h.from_date) >= new Date(new Date().toDateString()))
    .sort((a, b) => new Date(a.from_date) - new Date(b.from_date))[0];

  const sortedHistory = [...history].sort((a, b) => new Date(b.from_date) - new Date(a.from_date));
  const filteredHistory = histFilter === 'all'
    ? sortedHistory
    : sortedHistory.filter(h => h.status === histFilter);

  return (
    <div className="leaves-page">

      {loading ? (
        <div className="lv-loading"><div className="lv-spinner" /></div>
      ) : onProbation ? (

        /* ═══ PROBATION VIEW ═══ */
        <>
          <div className="lv-prob-hero">
            <div className="lv-prob-glow" />
            <div className="lv-prob-content">
              <div className="lv-prob-badge">Probation Period</div>
              <div className="lv-prob-title">Welcome aboard 👋</div>
              <div className="lv-prob-text">
                You're in your first {probation.probation_months || 3} months at Fotile.
                Leave entitlement begins once that period is complete — so there's
                nothing to apply for just yet.
              </div>

              <div className="lv-prob-bar-track">
                <div className="lv-prob-bar-fill" style={{ width: `${probationPct}%` }} />
              </div>

              <div className="lv-prob-dates">
                <div className="lv-prob-date">
                  <span className="lv-prob-date-lbl">Started</span>
                  <span className="lv-prob-date-val">{formatLongDate(probation.joining_date)}</span>
                </div>
                <div className="lv-prob-date lv-prob-date-right">
                  <span className="lv-prob-date-lbl">Leave unlocks</span>
                  <span className="lv-prob-date-val">{formatLongDate(probation.probation_ends)}</span>
                </div>
              </div>

              <div className="lv-prob-countdown">
                <span className="lv-prob-count-num">{probation.days_remaining}</span>
                <span className="lv-prob-count-lbl">
                  day{probation.days_remaining === 1 ? '' : 's'} to go
                </span>
              </div>
            </div>
          </div>

          <div className="lv-prob-info">
            <div className="lv-prob-info-title">What this means day to day</div>
            <div className="lv-prob-info-row">
              <span className="lv-prob-info-ico">🕘</span>
              <div>Keep clocking in and out as normal — your attendance is being
              recorded exactly the same way.</div>
            </div>
            <div className="lv-prob-info-row">
              <span className="lv-prob-info-ico">📋</span>
              <div>Casual, sick and annual leave all become available on{' '}
              <b>{formatLongDate(probation.probation_ends)}</b>, with the full
              yearly allowance.</div>
            </div>
            <div className="lv-prob-info-row">
              <span className="lv-prob-info-ico">💬</span>
              <div>If something urgent comes up before then, speak to HR directly —
              they can arrange it for you.</div>
            </div>
          </div>

          {/* History still shows, in case HR arranged something for them */}
          {history.length > 0 && (
            <>
              <div className="leaves-section-label" style={{ marginTop: 22 }}>Your Records</div>
              <div className="lv-history-list">
                {sortedHistory.map((h, i) => {
                  const meta   = LEAVE_TYPES.find(t => t.key === h.leave_type) || LEAVE_TYPES[0];
                  const status = STATUS_CFG[h.status] || STATUS_CFG.pending;
                  return (
                    <div key={i} className="lv-hist-card" style={{ borderLeftColor: status.color }}>
                      <div className="lv-hist-top">
                        <div className="lv-hist-type-row">
                          <span className="lv-hist-icon" style={{ background: `${meta.color}1a` }}>{meta.icon}</span>
                          <div>
                            <div className="lv-hist-type">{meta.label} Leave</div>
                            <div className="lv-hist-dates">
                              {formatShortDate(h.from_date)}
                              {h.from_date !== h.to_date ? ` – ${formatShortDate(h.to_date)}` : ''}
                              <span className="lv-hist-days"> · {h.total_days} day{h.total_days > 1 ? 's' : ''}</span>
                            </div>
                          </div>
                        </div>
                        <span className="lv-status-badge" style={{ background: status.bg, color: status.color }}>
                          {status.icon} {status.label}
                        </span>
                      </div>
                      {h.reason && <div className="lv-hist-reason">"{h.reason}"</div>}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>

      ) : (
        <>
          {/* ═══ HERO — total balance at a glance ═══ */}
          <div className="lv-hero">
            <div className="lv-hero-glow" />
            <div className="lv-hero-content">
              <div className="lv-hero-label">Total Leave Remaining</div>
              <div className="lv-hero-num">
                {totalRemaining}
                <span className="lv-hero-of">/ {totalDays} days</span>
              </div>
              <div className="lv-hero-bar-track">
                <div className="lv-hero-bar-fill" style={{ width: `${totalDays ? (totalRemaining / totalDays) * 100 : 0}%` }} />
              </div>
              {upcoming ? (
                <div className="lv-hero-upcoming">
                  <span className="lv-hero-upcoming-dot" />
                  Next leave: {formatShortDate(upcoming.from_date)} · {relativeTime(upcoming.from_date)}
                </div>
              ) : (
                <div className="lv-hero-upcoming lv-hero-upcoming-none">
                  No upcoming leave scheduled
                </div>
              )}
            </div>
          </div>

          {/* ═══ BALANCE CARDS ═══ */}
          <div className="leaves-section-label">Your Balance</div>
          <div className="lv-balance-grid">
            {balanceList.map((b, i) => {
              const meta = LEAVE_TYPES.find(t => t.key === b.type);
              const pct  = b.total > 0 ? Math.round((b.remaining / b.total) * 100) : 0;
              const used = b.total - b.remaining;
              const r = 24, circ = 2 * Math.PI * r;
              const offset = circ * (1 - pct / 100);
              const isEmpty = b.remaining === 0;
              return (
                <div key={i} className={`lv-bal-v2 lv-bal-${b.type} ${isEmpty ? 'lv-bal-empty' : ''}`}>
                  <div className="lv-bal-v2-watermark">{meta.icon}</div>
                  <div className="lv-bal-v2-top">
                    <span className="lv-bal-v2-label">{meta.label}</span>
                    <span className="lv-bal-v2-icon-badge">{meta.icon}</span>
                  </div>
                  <div className="lv-bal-v2-ring-row">
                    <svg width="58" height="58" viewBox="0 0 58 58">
                      <circle cx="29" cy="29" r={r} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="5" />
                      <circle cx="29" cy="29" r={r} fill="none"
                        stroke="#fff" strokeWidth="5"
                        strokeDasharray={circ}
                        strokeDashoffset={offset}
                        strokeLinecap="round"
                        transform="rotate(-90 29 29)"
                        style={{ transition: 'stroke-dashoffset 1s ease' }}
                      />
                    </svg>
                    <div className="lv-bal-v2-nums">
                      <div className="lv-bal-v2-remaining">{b.remaining}</div>
                      <div className="lv-bal-v2-sub">of {b.total} left</div>
                    </div>
                  </div>
                  <div className="lv-bal-v2-footer">
                    {isEmpty ? 'Fully used' : `${used} used this year`}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ═══ STATS STRIP ═══ */}
          {history.length > 0 && (
            <div className="lv-quick-stats">
              <div className="lv-qs-item">
                <span className="lv-qs-num" style={{ color: '#10b981' }}>{approvedCount}</span>
                <span className="lv-qs-lbl">Approved</span>
              </div>
              <div className="lv-qs-divider" />
              <div className="lv-qs-item">
                <span className="lv-qs-num" style={{ color: '#3b82f6' }}>{pendingCount}</span>
                <span className="lv-qs-lbl">Pending</span>
              </div>
              <div className="lv-qs-divider" />
              <div className="lv-qs-item">
                <span className="lv-qs-num" style={{ color: '#ef4444' }}>{rejectedCount}</span>
                <span className="lv-qs-lbl">Rejected</span>
              </div>
            </div>
          )}

          {message && <div className="lv-toast lv-toast-success">✓ {message}</div>}
          {error && !showForm && <div className="lv-toast lv-toast-error">⚠ {error}</div>}

          {/* ═══ CTA ═══ */}
          {!showForm && (
            <div className="lv-cta-banner" onClick={() => setShowForm(true)}>
              <div className="lv-cta-icon">🗓️</div>
              <div className="lv-cta-text">
                <div className="lv-cta-title">Need time off?</div>
                <div className="lv-cta-sub">Submit a request — HR reviews within 24 hours</div>
              </div>
              <div className="lv-cta-arrow">→</div>
            </div>
          )}

          {/* ═══ FORM ═══ */}
          {showForm && (
            <>
            <div className="leaves-section-label" style={{ marginTop: 4 }}>New Leave Request</div>
            <div className="leave-form-card lv-form-v2">

              <div className="lv-form-header">
                <div className="lv-form-header-icon">📝</div>
                <div>
                  <div className="lv-form-header-title">Request Details</div>
                  <div className="lv-form-header-sub">Fill in the information below</div>
                </div>
              </div>

              {error && <div className="lv-toast lv-toast-error" style={{ margin: '14px 18px 0' }}>⚠ {error}</div>}

              {/* Type selector */}
              <div className="form-group">
                <label className="form-label">Leave Type</label>
                <div className="lv-type-pills-v2">
                  {LEAVE_TYPES.map(t => {
                    const active = leaveType === t.key;
                    const bal    = balanceList.find(b => b.type === t.key);
                    return (
                      <button key={t.key} type="button"
                        className={`lv-type-pill-v2 ${active ? 'lv-type-v2-active' : ''}`}
                        style={ active ? {
                          background: `linear-gradient(135deg, ${t.color}dd, ${t.color})`,
                          borderColor: t.color,
                        } : {} }
                        onClick={() => setLeaveType(t.key)}>
                        {active && <span className="lv-type-check">✓</span>}
                        <span className="lv-type-icon-v2">{t.icon}</span>
                        <span>{t.label}</span>
                        <span className={`lv-type-bal ${active ? 'lv-type-bal-active' : ''}`}>
                          {bal ? `${bal.remaining} left` : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Dates */}
              <div className="form-group">
                <label className="form-label">Dates</label>
                <div className="lv-date-row">
                  <label className="lv-date-box">
                    <span className="lv-date-box-lbl">FROM</span>
                    <input type="date" className="lv-date-input" value={fromDate}
                      onChange={e => setFromDate(e.target.value)} />
                    {fromDate && <span className="lv-date-pretty">{formatFullDate(fromDate)}</span>}
                  </label>
                  <div className="lv-date-arrow">→</div>
                  <label className="lv-date-box">
                    <span className="lv-date-box-lbl">TO</span>
                    <input type="date" className="lv-date-input" value={toDate}
                      onChange={e => setToDate(e.target.value)} min={fromDate} />
                    {toDate && <span className="lv-date-pretty">{formatFullDate(toDate)}</span>}
                  </label>
                </div>
              </div>

              {/* Summary */}
              {requestedDays > 0 && (
                <div className={`lv-days-badge-v2 ${exceedsBalance ? 'lv-days-badge-error' : ''}`}>
                  <span className="lv-days-badge-num">{requestedDays}</span>
                  <span className="lv-days-badge-txt">
                    {exceedsBalance ? (
                      <>
                        day{requestedDays > 1 ? 's' : ''} requested — you only have{' '}
                        <strong>{selectedBal?.remaining}</strong> {selectedMeta?.label.toLowerCase()} day
                        {selectedBal?.remaining !== 1 ? 's' : ''} left
                      </>
                    ) : (
                      <>
                        day{requestedDays > 1 ? 's' : ''} of {selectedMeta?.label.toLowerCase()} leave requested
                        {selectedBal && ` · ${selectedBal.remaining - requestedDays} would remain`}
                      </>
                    )}
                  </span>
                </div>
              )}

              {/* Reason */}
              <div className="form-group">
                <div className="lv-reason-header">
                  <label className="form-label" style={{ marginBottom: 0 }}>Reason</label>
                  <span className="lv-char-count">{reason.length}/200</span>
                </div>
                <textarea className="form-control lv-textarea-v2" rows="3" maxLength={200}
                  placeholder="Briefly describe your reason for this leave..."
                  value={reason} onChange={e => setReason(e.target.value)} />
              </div>

              <div className="lv-form-btns">
                <button className="lv-cancel-btn" onClick={() => { setShowForm(false); setError(''); }}>
                  Cancel
                </button>
                <button className="lv-submit-v2" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? (
                    <span className="lv-submit-loading">
                      <span className="lv-submit-dot" /><span className="lv-submit-dot" /><span className="lv-submit-dot" />
                    </span>
                  ) : (
                    <>Submit Request →</>
                  )}
                </button>
              </div>
            </div>
            </>
          )}

          {/* ═══ HISTORY ═══ */}
          <div className="lv-hist-header">
            <div className="leaves-section-label" style={{ marginBottom: 0 }}>Leave History</div>
            {history.length > 0 && (
              <div className="lv-hist-filters">
                {[
                  { k: 'all',       l: 'All' },
                  { k: 'pending',   l: 'Pending' },
                  { k: 'approved',  l: 'Approved' },
                  { k: 'rejected',  l: 'Rejected' },
                  { k: 'cancelled', l: 'Cancelled' },
                ].map(f => (
                  <button key={f.k}
                    className={`lv-hist-filter ${histFilter === f.k ? 'lv-hist-filter-active' : ''}`}
                    onClick={() => setHistFilter(f.k)}>
                    {f.l}
                  </button>
                ))}
              </div>
            )}
          </div>

          {filteredHistory.length === 0 ? (
            <div className="leave-history-card">
              <div className="lv-empty">
                <div className="lv-empty-icon">🗓️</div>
                {history.length === 0 ? 'No leave requests yet' : `No ${histFilter} requests`}
              </div>
            </div>
          ) : (
            <div className="lv-history-list">
              {filteredHistory.map((h, i) => {
                const meta   = LEAVE_TYPES.find(t => t.key === h.leave_type) || LEAVE_TYPES[0];
                const status = STATUS_CFG[h.status] || STATUS_CFG.pending;
                const isFuture = new Date(h.from_date) >= new Date(new Date().toDateString());
                const canCancel = isUpcoming(h.from_date) &&
                                  (h.status === 'pending' || h.status === 'approved');
                return (
                  <div key={i} className={`lv-hist-card ${h.status === 'cancelled' ? 'lv-hist-cancelled' : ''}`}
                       style={{ borderLeftColor: status.color }}>
                    <div className="lv-hist-top">
                      <div className="lv-hist-type-row">
                        <span className="lv-hist-icon" style={{ background: `${meta.color}1a` }}>{meta.icon}</span>
                        <div>
                          <div className="lv-hist-type">
                            {meta.label} Leave
                            {isFuture && h.status === 'approved' && <span className="lv-hist-upcoming-tag">Upcoming</span>}
                          </div>
                          <div className="lv-hist-dates">
                            {formatShortDate(h.from_date)}
                            {h.from_date !== h.to_date ? ` – ${formatShortDate(h.to_date)}` : ''}
                            <span className="lv-hist-days"> · {h.total_days} day{h.total_days > 1 ? 's' : ''}</span>
                          </div>
                        </div>
                      </div>
                      <span className="lv-status-badge" style={{ background: status.bg, color: status.color }}>
                        {status.icon} {status.label}
                      </span>
                    </div>
                    {h.reason && <div className="lv-hist-reason">"{h.reason}"</div>}

                    {canCancel && (
                      <div className="lv-hist-actions">
                        <button
                          className="lv-hist-cancel-btn"
                          onClick={() => setConfirmCancel(h)}
                          disabled={cancelling === h.id}
                        >
                          {cancelling === h.id ? 'Cancelling…' : '⊘ Cancel this leave'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ═══ CANCEL CONFIRMATION ═══ */}
      {confirmCancel && (
        <div className="lv-confirm-overlay" onClick={() => setConfirmCancel(null)}>
          <div className="lv-confirm-box" onClick={e => e.stopPropagation()}>
            <div className="lv-confirm-icon">⊘</div>
            <div className="lv-confirm-title">Cancel this leave?</div>
            <div className="lv-confirm-text">
              {(LEAVE_TYPES.find(t => t.key === confirmCancel.leave_type) || LEAVE_TYPES[0]).label} Leave ·{' '}
              {formatShortDate(confirmCancel.from_date)}
              {confirmCancel.from_date !== confirmCancel.to_date ? ` – ${formatShortDate(confirmCancel.to_date)}` : ''}
              <br /><br />
              You will be expected at work on these dates, and HR will be notified.
            </div>
            <div className="lv-confirm-btns">
              <button className="lv-confirm-no" onClick={() => setConfirmCancel(null)}>Keep Leave</button>
              <button className="lv-confirm-yes" onClick={() => handleCancel(confirmCancel)}
                      disabled={cancelling === confirmCancel.id}>
                {cancelling === confirmCancel.id ? 'Cancelling…' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}